import { isFunction } from '@/lib/utils/typeUtils';
import { logger } from '@/lib/logger';
import { features, getAccessToken, getToken } from '@/lib';

type NotNullOnAuthorizeParameters = Parameters<
  NonNullable<NonNullable<typeof window.xprops>['onAuthorize']>
>;

// The below 2 types come from smartcomponentnodeweb Flow types
// https://github.paypal.com/Checkout-R/smartcomponentnodeweb/blob/develop/client/smart-payment-buttons/src/props/onAuth.js
export type OnAuthData = {
  accessToken?: string;
  appName?: string;
};

// https://github.paypal.com/Checkout-R/smartcomponentnodeweb/blob/develop/client/smart-payment-buttons/src/props/onApprove.js
type DirectApproveMessage = {
  messageName: 'direct-approve';
  orderID: string;
  approveData: NotNullOnAuthorizeParameters[0];
  authData?: OnAuthData;
};

/**
 * When Chrome freezes the parent window (merchant site), post-robot cannot
 * receive ACK messages and throws "No ack for postMessage" errors.
 *
 * We can attempt to send postMessage to the parent window.
 * When the user returns to the merchant tab, the parent window
 * receives the message and completes the flow.
 */
export async function xpropsOnAuthorizeWithOpenerFallback(
  ...args: NotNullOnAuthorizeParameters
): Promise<void> {
  try {
    return await window.xprops?.onAuthorize?.(...args);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const hasOpener = isFunction(window.opener);
    const isFallbackEnabled = features.isActive(
      'shouldUseXpropsOpenerFallback',
    );
    logger.warn('xprops_on_authorize_error', {
      hasOpener,
      isFallbackEnabled,
      message,
    });

    if (
      isFallbackEnabled &&
      message.includes('No ack') &&
      isFunction(window.opener)
    ) {
      logger.info('xprops_on_authorize_no_ack_retry', { message });
      logger.fpti('xprops_on_authorize_no_ack_retry', {
        event_name: 'process_on_authorize_no_ack_retry',
      });

      const approveData = args[0];
      const directApprovalMessage = {
        approveData,
        authData: {
          accessToken: getAccessToken().value ?? undefined,
          appName: 'weasley',
        },
        messageName: 'direct-approve',
        orderID: approveData.orderID ?? getToken(),
      } satisfies DirectApproveMessage;

      try {
        return await window.opener.postMessage(
          directApprovalMessage,
          window.origin,
        );
      } catch (postToOpenerError) {
        const openerError =
          postToOpenerError instanceof Error
            ? postToOpenerError
            : new Error(String(postToOpenerError));
        logger.error('xprops_on_authorize_no_ack_retry_error', {
          ...openerError,
        });
        logger.fpti('xprops_on_authorize_no_ack_retry_error', {
          error: openerError.message,
          event_name: 'process_on_authorize_no_ack_retry_error',
        });

        throw err;
      }
    }
    throw err;
  }
}
